<template>
  <v-autocomplete
    v-model="selectedItems"
    :label="label"
    :items="items"
    :placeholder="placeholder"
    :disabled="disabled"
    item-title="text"
    item-value="id"
    multiple
    clearable
    :chips="chips"
    :closable-chips="chips"
    variant="outlined"
    density="compact"
    :custom-filter="customFilter"
    @update:model-value="emitNewValue"
  >
    <template v-if="chips" v-slot:chip="{ props, item }">
      <v-chip clearable v-bind="props" :text="getChipText(item.raw)"></v-chip>
    </template>
  </v-autocomplete>
</template>

<script setup lang="ts">
import { onBeforeMount, onMounted, ref, type Ref } from "vue";
import type { SelectItem } from "@/models/selectItem";

const props = withDefaults(
  defineProps<{
    items: SelectItem[] | string[];
    selectItems: string[];
    placeholder?: string;
    label?: string;
    disabled?: boolean;
    chips: boolean;
  }>(),
  {
    chips: false,
  }
);

const emit = defineEmits<{
  (e: "fieldUpdated", newItems: string[]): void;
}>();

const selectedItems = ref(new Array<string>());
const itemsAsSelectItems: Ref<SelectItem[]> = ref([]);

onBeforeMount(() => {
  itemsAsSelectItems.value =
    props.items.length == 0 || typeof props.items[0] == "string"
      ? generateSelectItemsFromString(props.items as string[])
      : (props.items as SelectItem[]);

  selectedItems.value = getItems(
    (props.selectItems as string[]) ?? new Array<string>()
  );
});

onMounted(() => {
  emit("fieldUpdated", selectedItems.value);
});

function emitNewValue(newItems: string[]): void {
  selectedItems.value = newItems ?? "";
  emit("fieldUpdated", newItems);
}

function customFilter(item: string, queryText: string): boolean {
  return item.toLocaleLowerCase().startsWith(queryText.toLocaleLowerCase());
}

function getItems(searchItems: string[]): string[] {
  const _items = new Array<string>();
  searchItems.forEach((searchItem: string) => {
    const idx = itemsAsSelectItems.value.findIndex(
      (item: SelectItem) =>
        item.id.toLocaleLowerCase() === searchItem.toLocaleLowerCase()
    );
    if (idx !== -1) {
      _items.push(itemsAsSelectItems.value[idx].id);
    }
  });
  return _items;
}

function generateSelectItemsFromString(stringItems: string[]): SelectItem[] {
  return stringItems.map((s: string) => {
    return {
      id: s,
      text: s,
    } as SelectItem;
  });
}

function getChipText(rawItem: string | SelectItem): string {
  return typeof rawItem === "string" ? rawItem : rawItem.text;
}
</script>
